import { useCallback, useEffect, useRef, type ComponentType } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Carousel, CarouselContent } from '@/components/ui/carousel'
import { HeartHandshake } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NeedsAnalysisProvider } from '@/contexts/needs-analysis/NeedsAnalysisProvider'
import { useNeedsAnalysis } from '@/contexts/needs-analysis/NeedsAnalysisContext'
import { SCREEN_TYPES, type ScreenType } from '@/contexts/needs-analysis/useScreenSequence'
import { useCarouselSync } from '@/hooks/useCarouselSync'
import { FOCUSABLE_SELECTOR } from '@/lib/focus'
import IntroScreen from '@/components/onboarding/IntroScreen'
import PreferredLanguageScreen from '@/components/onboarding/PreferredLanguageScreen'
import EnglishAbilityScreen from '@/components/onboarding/EnglishAbilityScreen'
import WeeklyHoursScreen from '@/components/onboarding/WeeklyHoursScreen'
import PrimaryGoalsScreen from '@/components/onboarding/PrimaryGoalsScreen'
import CareerInterestsScreen from '@/components/onboarding/CareerInterestsScreen'
import TestTypeScreen from '@/components/onboarding/TestTypeScreen'
import CommunicationChannelsScreen from '@/components/onboarding/CommunicationChannelsScreen'
import CommunicationFrequencyScreen from '@/components/onboarding/CommunicationFrequencyScreen'
import DoneScreen from '@/components/onboarding/DoneScreen'
import OnboardingNavigationFooter from '@/components/onboarding/OnboardingNavigationFooter'

type Props = {
  readonly open: boolean
  onOpenChange: (open: boolean) => void
}

const SCREEN_COMPONENTS: Record<ScreenType, ComponentType> = {
  [SCREEN_TYPES.INTRO]: IntroScreen,
  [SCREEN_TYPES.LANGUAGE]: PreferredLanguageScreen,
  [SCREEN_TYPES.ENGLISH]: EnglishAbilityScreen,
  [SCREEN_TYPES.TIME]: WeeklyHoursScreen,
  [SCREEN_TYPES.GOALS]: PrimaryGoalsScreen,
  [SCREEN_TYPES.INDUSTRIES]: CareerInterestsScreen,
  [SCREEN_TYPES.TEST_TYPE]: TestTypeScreen,
  [SCREEN_TYPES.COMMUNICATION_CHANNELS]: CommunicationChannelsScreen,
  [SCREEN_TYPES.COMMUNICATION_FREQUENCY]: CommunicationFrequencyScreen,
  [SCREEN_TYPES.DONE]: DoneScreen,
}

export default function NeedsAnalysisModal({ open, onOpenChange }: Props) {
  return (
    <NeedsAnalysisProvider onSubmitSuccess={() => onOpenChange(false)}>
      <NeedsAnalysisModalContent open={open} onOpenChange={onOpenChange} />
    </NeedsAnalysisProvider>
  )
}

function NeedsAnalysisModalContent({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const {
    currentStep,
    totalSteps,
    screens,
    isSubmitting,
    nextStep,
    previousStep,
    canProceedFromCurrentStep,
    isFormValid,
    submitSurvey,
    reset,
  } = useNeedsAnalysis()

  const { setApi } = useCarouselSync(currentStep, open)
  const dialogContentRef = useRef<HTMLDivElement>(null)

  // Capture-phase Tab handler that implements correct focus wrapping.
  // Radix Dialog's built-in FocusScope does not account for `inert` subtrees
  // when computing tabbable edges, so Tab/Shift+Tab silently fails on slides
  // other than the first. This handler intercepts before Radix's bubble-phase
  // handler and wraps focus only among truly visible (non-inert) elements.
  const handleTabKeyCapture = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return

    const content = dialogContentRef.current
    if (!content) return

    const tabbable = Array.from(content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.closest('[inert]')
    )

    if (tabbable.length === 0) return

    const first = tabbable[0]
    const last = tabbable[tabbable.length - 1]
    const active = document.activeElement

    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  const isIntroScreen = currentStep === 0
  const isDoneScreen = currentStep === totalSteps - 1

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        onKeyDownCapture={handleTabKeyCapture}
        // The default target is the first tabbable, which sits in a slide the carousel is about to make inert.
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          dialogContentRef.current?.focus()
        }}
        className='max-h-[90vh] w-[calc(100vw-2rem)] max-w-[95vw] bg-white p-4 sm:max-w-4xl sm:p-6 flex flex-col'
      >
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle className='flex items-center gap-2 text-base sm:text-lg'>
            <HeartHandshake strokeWidth={2} className='h-4 w-4 sm:h-5 sm:w-5 text-pink-500' aria-hidden='true' />
            {t('needsAnalysis.modalTitle')}
          </DialogTitle>
          <DialogDescription className='sr-only'>{t('needsAnalysis.modalDescription')}</DialogDescription>
        </DialogHeader>

        <div className='relative mt-2 w-full flex-1 overflow-hidden sm:mt-4'>
          <div className='pointer-events-none absolute top-0 bottom-0 start-0 z-10 w-4 ltr:bg-gradient-to-r rtl:bg-gradient-to-l from-white to-transparent sm:w-32' />
          <div className='pointer-events-none absolute top-0 end-0 bottom-0 z-10 w-4 ltr:bg-gradient-to-l rtl:bg-gradient-to-r from-white to-transparent sm:w-32' />

          <Carousel
            aria-label={t('needsAnalysis.carouselLabel')}
            setApi={setApi}
            opts={{
              watchDrag: false,
              // Embla "duration" is unitless scroll speed (not ms). 10 keeps the
              // animation fast so the 'settle' event fires quickly, which is required
              // for focus management in useCarouselSync to feel immediate.
              duration: 10,
            }}
          >
            <CarouselContent className='mx-auto h-[calc(90vh-180px)] w-full sm:h-[450px] sm:max-w-2xl'>
              {screens.map((screen) => {
                const Screen = SCREEN_COMPONENTS[screen]
                return <Screen key={screen} />
              })}
            </CarouselContent>
          </Carousel>
        </div>

        <OnboardingNavigationFooter
          current={currentStep}
          totalScreens={totalSteps}
          isIntroScreen={isIntroScreen}
          isDoneScreen={isDoneScreen}
          canProceed={canProceedFromCurrentStep()}
          isFormValid={isFormValid()}
          isSubmitting={isSubmitting}
          onPrevious={previousStep}
          onNext={nextStep}
          onFinish={submitSurvey}
        />
      </DialogContent>
    </Dialog>
  )
}
